import React, { useState, useMemo } from "react";
import CustomTable from "components/Table/CustomTable";
import { useModal } from "contexts/ModalProvider";
import { useToasts } from "utils/useHotToast";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import dayjs from "dayjs";
import Image from "components/Common/Image";
import { getImageUrl, ImageSourceType } from "utils/imageUtils";
import { useNavigate } from "react-router-dom";

// Custom Hooks
import useTableChangeHandler from "hooks/useTableChangeHandler";
import ToastNotification from "components/Toast/ToastNotification";

import { formatCurrency } from "utils/formatters";
import { AffiliateProductsSchema } from "model/products/affiliate.schema";
import { useDeleteProductMutation } from "services/products/api";

const AffiliateProductsTable = ({ data, error, isLoading, onTableChange }) => {
  const navigate = useNavigate();
  const { modal } = useModal();
  const { handleSort, handleFilter, handleSearch, handlePageChange } = useTableChangeHandler(onTableChange);
  const { addToast, removeToast } = useToasts();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [deleteProduct] = useDeleteProductMutation();

  const columns = useMemo(
    () => [
      {
        header: "รายการ Product Affiliate",
        accessor: "name",
        sortable: true,
        render: (item) => (
          <div className="product-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
            <div>
              <div className="text-lg fw-semibold">{item.name}</div>
              <div className="text-md text-gray-500">SKU: {item.sku}</div>
            </div>
          </div>
        ),
      },
      {
        header: "แบรนด์ F&N",
        accessor: "brand",
        responsive: "md",
        render: (item) => (
          <div className="brand-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.brand?.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.brand?.name} />
            <div className="text-lg">{item.brand?.name}</div>
          </div>
        ),
      },
      {
        header: "หมวดหมู่สินค้า",
        accessor: "category",
        responsive: "lg",
        render: (item) => <div className="text-success-500 fw-semibold">{item.category?.name}</div>,
      },
      {
        header: "ราคา",
        accessor: "price",
        sortable: true,
        align: "end",
        render: (item) => formatCurrency(item.price),
      },
      {
        header: "วันที่สร้างสินค้า",
        accessor: "createdDate",
        responsive: "xl",
        render: (item) => dayjs(item.createdAt).format("YYYY-MM-DD"),
      },
      {
        header: "",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <div
            className="text-gray-500 cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              handleDelete(item);
            }}
          >
            <i className="bx bx-trash"></i>
          </div>
        ),
      },
    ],
    []
  );

  const handleDelete = async (item) => {
    const result = await modal.alert.danger({
      title: "ยืนยันที่จะนำสินค้า Affiliate ออกจากระบบ",
      message: `ตรวจสอบรายละเอียดรายการสินค้าที่จะนำสินค้า Affiliate ออกจากระบบ`,
      confirmText: "ยืนยันที่จะนำออก",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-2">
          <div className="product-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
            <div>
              <div className="text-lg fw-semibold">{item.name}</div>
              <div className="text-md text-gray-500">SKU: {item.id}</div>
            </div>
          </div>
        </div>
      ),
    });
    if (result) {
      try {
        await deleteProduct(item);
        const toastId = addToast(
          <ToastNotification
            //
            icon={check}
            title="ลบสินค้าสำเร็จ"
            description={`ลบสินค้า ${item.name} ออกจาก Affiliate เรียบร้อยแล้ว`}
            primaryButtonText="ดูสินค้าจาก NomAroi"
            onPrimaryClick={() => {
              removeToast(toastId);
              navigate("/products?tab=nomaroi");
            }}
            onSecondaryClick={() => removeToast(toastId)}
            variant="success"
          />,
          {
            autoDismiss: true,
          }
        );
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
      } catch (error) {
        console.error("Failed to delete product from affiliate:", error);
        const toastId = addToast(<ToastNotification icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถลบสินค้าออกจาก Affiliate ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} variant="danger" />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleDeleteAll = async (items) => {
    const result = await modal.alert.danger({
      title: "ยืนยันที่จะนำสินค้า Affiliate ออกจากระบบ",
      message: `คุณได้ทำการกดลบรายการสินค้า Affiliate ที่มีอยู่ในระบบทั้งหมด “${items.length} รายการ”`,
      confirmText: "ยืนยันที่จะนำออก",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-3 divide-y">
          {items.map((item, index) => (
            <div key={item.productId} className={`d-flex flex-column gap-2 ${index > 0 && "pt-3"}`}>
              <div className="product-info d-flex align-items-center gap-3">
                <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
                <div>
                  <div className="text-lg fw-semibold">{item.name}</div>
                  <div className="text-md text-gray-500">SKU: {item.id}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ),
    });
    if (result) {
      try {
        await deleteProduct(items);
        const toastId = addToast(<ToastNotification variant="success" icon={check} title={`นำสินค้า Affiliate ออกจากระบบแล้ว ${items.length} รายการ`} description="สามารถไปตรวจสอบสินค้าที่นำออกไปแล้วได้ที่ สินค้าจาก NomAroi" primaryButtonText="ดูสินค้าจาก NomAroi" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
      } catch (error) {
        console.error("Failed to delete products from affiliate:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถลบสินค้าออกจาก Affiliate ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleSelectionChange = (selected) => {
    setSelectedProducts(selected);
  };

  return (
    <div>
      <div className="text-lg mb-4">รายการ Product ที่ถูกเพิ่มแล้วในระบบทั้งหมด *{data?.pagination?.total?.toLocaleString() || 0} รายการ *</div>

      <CustomTable
        keyField="id"
        data={data?.data || []}
        columns={columns}
        selectable={true}
        onSelectionChange={handleSelectionChange}
        selectionActions={
          <a
            className="danger"
            onClick={() => {
              if (selectedProducts.length > 0) {
                handleDeleteAll(selectedProducts);
              }
            }}
          >
            <i className="bx bx-trash" />
            <span className="d-none d-md-block">ลบรายการที่เลือกไว้</span>
          </a>
        }
        // onRowClick={item => console.log("Row clicked:", item)}
        className="affiliate-table"
        itemsPerPage={data?.pagination?.limit || 10}
        totalItems={data?.pagination?.total || 0}
        onSort={handleSort}
        onFilter={handleFilter}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"}
        errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}
        emptyMessage="ไม่พบข้อมูลสินค้า Affiliate"
      />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    try {
      if (!props.isLoading && !props.error && props.data) {
        schema.validateSync(props.data);
      }
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(AffiliateProductsTable, AffiliateProductsSchema);

export default ValidatedTable;
